/**
 * ISO 4217 — the complete set of active alphabetic currency codes.
 *
 * This is a UNIVERSAL standard, not a country coupling: every assigned code lives here (BRL among them, no
 * differently from USD or EUR), and the app-level `Money`/currency validators use it to reject a code that
 * merely *looks* right (three uppercase letters) but is UNASSIGNED, e.g. `AAA`. The DB `CHECK` on money
 * columns stays shape-only (`^[A-Z]{3}$`); this table is the authoritative gate where Money is minted.
 *
 * Included beyond national currencies: the reserved test code `XTS` and the "no currency" code `XXX`, the
 * precious-metal codes (XAU/XAG/XPT/XPD), the supranational/fund codes (XDR, XUA, XSU, XBA–XBD, XAF, XCD,
 * XOF, XPF, XCG), and recently-introduced codes (e.g. SLE, VES/VED, ZWG). Membership is O(1) via a Set.
 *
 * Source: ISO 4217 published active code list. Kept sorted for review; add/retire a code as the standard
 * changes. Erring toward inclusion of a just-superseded code is safe (it never falsely rejects a real one).
 */
export const ISO_4217_CURRENCIES = new Set([
    'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
    'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BOV',
    'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
    'CAD', 'CDF', 'CHE', 'CHF', 'CHW', 'CLF', 'CLP', 'CNY', 'COP', 'COU',
    'CRC', 'CUC', 'CUP', 'CVE', 'CZK',
    'DJF', 'DKK', 'DOP', 'DZD',
    'EGP', 'ERN', 'ETB', 'EUR',
    'FJD', 'FKP',
    'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
    'HKD', 'HNL', 'HTG', 'HUF',
    'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK',
    'JMD', 'JOD', 'JPY',
    'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
    'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
    'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR',
    'MWK', 'MXN', 'MXV', 'MYR', 'MZN',
    'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
    'OMR',
    'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
    'QAR',
    'RON', 'RSD', 'RUB', 'RWF',
    'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD',
    'SSP', 'STN', 'SVC', 'SYP', 'SZL',
    'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS',
    'UAH', 'UGX', 'USD', 'USN', 'UYI', 'UYU', 'UYW', 'UZS',
    'VED', 'VES', 'VND', 'VUV',
    'WST',
    'XAF', 'XAG', 'XAU', 'XBA', 'XBB', 'XBC', 'XBD', 'XCD', 'XCG', 'XDR',
    'XOF', 'XPD', 'XPF', 'XPT', 'XSU', 'XTS', 'XUA', 'XXX',
    'YER',
    'ZAR', 'ZMW', 'ZWG',
]);
/**
 * True when `code` is an ASSIGNED ISO 4217 alphabetic code (exact, case-sensitive uppercase). Callers that
 * also want the shape rule should test `/^[A-Z]{3}$/` first; a non-string or wrong-shape input returns
 * `false` here as well (it is simply not a member).
 */
export function isIso4217Currency(code) {
    return ISO_4217_CURRENCIES.has(code);
}
//# sourceMappingURL=iso4217.js.map